import { useEffect, useState } from 'react'
import { agendaLabService } from '../../services/api'

const emptyService = {
  name: '',
  description: '',
  price: 0,
  durationMinutes: 30,
  bufferBeforeMinutes: 0,
  bufferAfterMinutes: 0,
  isActive: true
}

const asArray = (value) => {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.items)) return value.items
  if (Array.isArray(value?.data)) return value.data
  if (Array.isArray(value?.$values)) return value.$values
  return []
}

const formatSupportTime = (before, after) => {
  const parts = []
  if (before > 0) parts.push(`Bloquea ${before} min antes`)
  if (after > 0) parts.push(`Bloquea ${after} min despues`)
  return parts.length > 0 ? parts.join(' / ') : 'Sin bloqueo adicional'
}

const formatCurrency = (value) => new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  maximumFractionDigits: 0
}).format(Number(value || 0))

export default function AgendaLabServices() {
  const [services, setServices] = useState([])
  const [form, setForm] = useState(emptyService)
  const [editingId, setEditingId] = useState(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [saving, setSaving] = useState(false)

  const load = async () => {
    try {
      setError('')
      const result = await agendaLabService.getServices()
      setServices(asArray(result.data))
    } catch (err) {
      setError(err?.message || 'No se pudieron cargar los servicios.')
      setServices([])
    }
  }

  useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setForm(emptyService)
    setEditingId(null)
  }

  const startEdit = (service) => {
    setError('')
    setSuccess('')
    setEditingId(service.id)
      setForm({
        name: service.name || '',
        description: service.description || '',
        price: Number(service.price || 0),
        durationMinutes: service.durationMinutes || 30,
        bufferBeforeMinutes: service.bufferBeforeMinutes || 0,
        bufferAfterMinutes: service.bufferAfterMinutes || 0,
      isActive: service.isActive ?? true
    })
  }

  const submit = async (e) => {
    e.preventDefault()
    try {
      setSaving(true)
      setError('')
      setSuccess('')

      if (editingId) {
        await agendaLabService.updateService(editingId, form)
        setSuccess('Servicio actualizado correctamente.')
      } else {
        await agendaLabService.createService(form)
        setSuccess('Servicio creado correctamente.')
      }

      resetForm()
      await load()
    } catch (err) {
      setError(err?.message || (editingId ? 'No se pudo actualizar el servicio.' : 'No se pudo crear el servicio.'))
    } finally {
      setSaving(false)
    }
  }

  const remove = async (service) => {
    try {
      setError('')
      setSuccess('')
      await agendaLabService.deleteService(service.id)
      if (editingId === service.id) {
        resetForm()
      }
      setSuccess('Servicio eliminado correctamente.')
      await load()
    } catch (err) {
      setError(err?.message || 'No se pudo eliminar el servicio.')
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Servicios</h1>
        <p className="text-sm text-gray-500 mt-1">
          Configura cada servicio con su descripcion, valor, duracion y tiempos que deben quedar bloqueados antes o despues de la cita.
        </p>
      </div>

      {error && <div className="card p-3 text-sm text-red-700 bg-red-50 border border-red-200">{error}</div>}
      {success && <div className="card p-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200">{success}</div>}

      <form className="card p-4 grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={submit}>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Nombre del servicio</label>
          <input
            className="input"
            placeholder="Ej. Consulta inicial"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Valor</label>
          <input
            className="input"
            type="number"
            min="0"
            step="0.01"
            placeholder="Valor del servicio"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: Number(e.target.value) })}
            required
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Duracion</label>
          <input
            className="input"
            type="number"
            min="1"
            placeholder="Duracion en minutos"
            value={form.durationMinutes}
            onChange={(e) => setForm({ ...form, durationMinutes: Number(e.target.value) })}
            required
          />
        </div>

        <div className="md:col-span-2 space-y-2">
          <label className="block text-sm font-medium text-gray-700">Descripcion</label>
          <textarea
            className="input min-h-[110px]"
            placeholder="Describe claramente que incluye este servicio y que debe saber el cliente."
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Bloquear tiempo antes</label>
          <input
            className="input"
            type="number"
            min="0"
            placeholder="Minutos previos"
            value={form.bufferBeforeMinutes}
            onChange={(e) => setForm({ ...form, bufferBeforeMinutes: Number(e.target.value) })}
          />
          <p className="text-xs text-gray-500">Minutos que la agenda debe bloquear antes de iniciar la cita.</p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Bloquear tiempo despues</label>
          <input
            className="input"
            type="number"
            min="0"
            placeholder="Minutos posteriores"
            value={form.bufferAfterMinutes}
            onChange={(e) => setForm({ ...form, bufferAfterMinutes: Number(e.target.value) })}
          />
          <p className="text-xs text-gray-500">Minutos que la agenda debe bloquear al terminar la cita.</p>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          />
          Servicio activo
        </label>

        <div className="md:col-span-2 flex flex-wrap gap-3">
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Agregar servicio'}
          </button>
          {editingId && (
            <button className="btn btn-secondary" type="button" onClick={resetForm}>
              Cancelar edicion
            </button>
          )}
        </div>
      </form>

      <div className="card p-4 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-2 pr-4">Servicio</th>
              <th className="py-2 pr-4">Descripcion</th>
              <th className="py-2 pr-4">Valor</th>
              <th className="py-2 pr-4">Duracion</th>
              <th className="py-2 pr-4">Bloqueo de agenda</th>
              <th className="py-2 pr-4">Estado</th>
              <th className="py-2 pr-4">Uso</th>
              <th className="py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {services.map((service) => (
              <tr key={service.id} className="border-t align-top">
                <td className="py-3 pr-4 font-medium text-gray-900">{service.name}</td>
                <td className="py-3 pr-4 text-gray-600">{service.description || 'Sin descripcion'}</td>
                <td className="py-3 pr-4 text-gray-600">{formatCurrency(service.price)}</td>
                <td className="py-3 pr-4 text-gray-600">{service.durationMinutes} min</td>
                <td className="py-3 pr-4 text-gray-600">
                  {formatSupportTime(service.bufferBeforeMinutes, service.bufferAfterMinutes)}
                </td>
                <td className="py-3 pr-4">
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${service.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                    {service.isActive ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td className="py-3 pr-4 text-gray-600">
                  {service.appointmentCount > 0 ? `${service.appointmentCount} cita(s)` : 'Sin citas'}
                </td>
                <td className="py-3 text-right">
                  <div className="flex justify-end gap-3">
                    <button
                      className="text-primary-600 hover:text-primary-700"
                      type="button"
                      onClick={() => startEdit(service)}
                    >
                      Editar
                    </button>
                    <button
                      className={service.canDelete ? 'text-red-600 hover:text-red-700' : 'text-gray-400 cursor-not-allowed'}
                      type="button"
                      disabled={!service.canDelete}
                      title={service.canDelete ? 'Eliminar servicio' : 'No se puede eliminar porque ya tiene citas asociadas'}
                      onClick={() => remove(service)}
                    >
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {services.length === 0 && (
              <tr>
                <td className="py-6 text-center text-gray-500" colSpan="8">
                  Aun no tienes servicios creados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
